// Copyright 2016 Sorama B.V.

import { Point2D, Range, Spectrum } from "datatypes/SoramaLocalApi";
import { Unit, UnitValue } from "units/Unit";

export class UnitSpectrum implements Spectrum {
    //#region Spectrum Members
    public data: Point2D[];
    public get properties(): { timeRange: Range } {
        return { timeRange: this.timeRange };
    }
    //#endregion

    private _xUnit: Unit;
    private _yUnit: Unit;
    private timeRange: Range;

    public get xUnit(): Unit {
        return this._xUnit;
    }

    public get yUnit(): Unit {
        return this._yUnit;
    }

    public get length(): number {
        return this.data.length;
    }

    //#region Constructors

    /**
     * Create a new unit spectrum.
     * @param xUnit The unit of the x values of the spectrum.
     * @param yUnit The unit of the y values of the spectrum.
     * @param dataPoints The data of this spectrum.
     * @param time The timestamp of this spectrum.
     */
    constructor(xUnit: Unit, yUnit: Unit, data: Point2D[], timeRange: Range) {
        this.data = data;

        this.timeRange = timeRange;
        this._yUnit = yUnit;
        this._xUnit = xUnit;
    }

    public static FromSpectrum(xUnit: Unit, yUnit: Unit, spectrum: Spectrum): UnitSpectrum {
        return new UnitSpectrum(xUnit, yUnit, spectrum.data, spectrum.properties.timeRange);
    }

    //#endregion

    /**
     * Get X unit value of the given index in the spectrum
     * @param index Index of the x value to get
     */
    public getXUnitValue(index: number): UnitValue {
        return new UnitValue(this.data[index].x, this._xUnit);
    }

    /**
     * Get Y unit value of the given index in the spectrum
     * @param index Index of the y value to get
     */
    public getYUnitValue(index: number): UnitValue {
        return new UnitValue(this.data[index].y, this._yUnit);
    }

    /**
     * Creates a copy of this UnitSpectrum
     */
    public copy(): UnitSpectrum {
        const newDataPoints: Point2D[] = [];
        for (let i = 0; i < this.data.length; i++) {
            newDataPoints.push({
                x: this.data[i].x,
                y: this.data[i].x,
            });
        }

        return new UnitSpectrum(this._xUnit, this._yUnit, newDataPoints, this.timeRange);
    }

    /**
     * Converts this UnitSpectrum to a UnitSpectrum with specified units
     * @param xUnit The unit for the new UnitSpectrum's x data
     * @param yUnit The unit for the new UnitSpectrum's y data
     */
    public convertToUnits(xUnit: Unit, yUnit: Unit): UnitSpectrum {
        // Preallocate array for speed
        const newDataPoints: Point2D[] = new Array(this.data.length);

        const xValue = new UnitValue(0, this.xUnit);
        const yValue = new UnitValue(0, this.yUnit);

        for (let i = 0; i < this.data.length; i++) {
            const dp = this.data[i];
            xValue.value = dp.x;
            yValue.value = dp.y;

            newDataPoints[i] = <Point2D>{ x: xValue.toUnit(xUnit), y: yValue.toUnit(yUnit) };
        }

        return new UnitSpectrum(xUnit, yUnit, newDataPoints, this.timeRange);
    }
}
